import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Check, Clock, Gavel, Send, ShieldQuestion } from 'lucide-react';
import { ApiClientError, EmptyState, Money, Spinner, enumLabel, relativeTime } from '@riskpay/ui-components';
import { api } from '../lib/client';

/* ==========================================================================
 *  Disputes
 *
 *  Money already paid out being claimed back. The screen is built around the
 *  one thing that is actually urgent — the response deadline — because a
 *  dispute that is lost by default is lost for a reason nobody chose.
 *
 *  A merchant can read a case and answer it. Raising and resolving belong to
 *  the acquirer side, and are done from the operations console.
 * ========================================================================== */

interface Dispute {
  id: string;
  status: 'OPEN' | 'EVIDENCE_REQUIRED' | 'UNDER_REVIEW' | 'WON' | 'LOST' | 'WITHDRAWN';
  category: string;
  reason_code: string | null;
  amount: { amount: string; currency: string; formatted: string };
  payment_id: string | null;
  payment_method: string | null;
  evidence: { narrative: string | null; submitted_at: string | null };
  evidence_due_at: string | null;
  days_to_respond: number | null;
  resolution_note: string | null;
  created_at: string;
  resolved_at: string | null;
}

interface Response {
  data: Dispute[];
  summary: {
    total: number;
    open: number;
    needs_evidence: number;
    at_risk: { amount: string; currency: string; formatted: string };
  };
}

const FILTERS = [
  { value: 'OPEN', label: 'Needs attention' },
  { value: 'UNDER_REVIEW', label: 'With the issuer' },
  { value: 'WON', label: 'Won' },
  { value: 'LOST', label: 'Lost' },
  { value: 'ALL', label: 'All' },
] as const;

function StatusPill({ status }: { status: Dispute['status'] }) {
  const map: Record<Dispute['status'], { label: string; className: string }> = {
    EVIDENCE_REQUIRED: { label: 'Evidence needed', className: 'badge-danger' },
    OPEN: { label: 'Open', className: 'badge-warning' },
    UNDER_REVIEW: { label: 'With the issuer', className: 'badge-info' },
    WON: { label: 'Won', className: 'badge-success' },
    LOST: { label: 'Lost', className: 'badge-danger' },
    WITHDRAWN: { label: 'Withdrawn', className: 'badge-neutral' },
  };
  const { label, className } = map[status];
  return <span className={`badge ${className}`}>{label}</span>;
}

/** The deadline, said the way a person would say it. */
function Deadline({ days }: { days: number | null }) {
  if (days === null) return null;
  const tone = days < 0 ? 'text-danger-text' : days <= 2 ? 'text-warning-text' : 'text-content-subtle';
  const text =
    days < 0 ? `${Math.abs(days)} day${Math.abs(days) === 1 ? '' : 's'} overdue`
    : days === 0 ? 'Due today'
    : `${days} day${days === 1 ? '' : 's'} left`;
  return (
    <span className={`flex items-center gap-1.5 text-xs ${tone}`}>
      <Clock className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
      {text}
    </span>
  );
}

export function Disputes() {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<string>('OPEN');
  const [openId, setOpenId] = useState<string | null>(null);
  const [narrative, setNarrative] = useState('');
  const [error, setError] = useState<string | null>(null);

  const disputes = useQuery({
    queryKey: ['disputes', status],
    queryFn: () => api.get<Response>(`/disputes?status=${status}`),
  });

  const submit = useMutation({
    mutationFn: (id: string) => api.post(`/disputes/${id}/evidence`, { narrative: narrative.trim() }),
    onSuccess: () => {
      setOpenId(null);
      setNarrative('');
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['disputes'] });
    },
    onError: (err) => setError(err instanceof ApiClientError ? err.message : 'Could not submit that response.'),
  });

  const rows = disputes.data?.data ?? [];
  const summary = disputes.data?.summary;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold tracking-tight">Disputes &amp; chargebacks</h2>
        <p className="mt-1.5 max-w-2xl text-sm leading-relaxed text-content-muted">
          When a cardholder claims a payment back, the amount is held out of your available balance until the case
          closes. Answer before the deadline — an unanswered dispute is decided against you automatically.
        </p>
      </div>

      {summary && summary.total > 0 && (
        <dl className="grid gap-px overflow-hidden rounded-lg border border-line bg-line sm:grid-cols-3">
          {[
            ['Open cases', String(summary.open), summary.open > 0],
            ['Awaiting your response', String(summary.needs_evidence), summary.needs_evidence > 0],
            ['Held from your balance', summary.at_risk.formatted, false],
          ].map(([label, value, alert]) => (
            <div key={label as string} className="bg-surface-raised px-5 py-4">
              <dt className="text-2xs uppercase tracking-label text-content-subtle">{label}</dt>
              <dd className={`numeral mt-1.5 text-xl font-semibold tracking-tight ${alert ? 'text-warning-text' : 'text-content'}`}>
                {value}
              </dd>
            </div>
          ))}
        </dl>
      )}

      <div className="flex flex-wrap items-center gap-2 border-b border-line pb-3">
        <div className="flex rounded-md border border-line bg-surface-inset p-0.5" role="group" aria-label="Filter">
          {FILTERS.map((f) => (
            <button
              key={f.value}
              type="button"
              onClick={() => setStatus(f.value)}
              aria-pressed={status === f.value}
              className={`rounded px-3 py-1.5 text-2xs font-medium transition-colors ${
                status === f.value ? 'bg-surface-overlay text-content' : 'text-content-subtle hover:text-content-muted'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <p role="alert" className="flex items-start gap-2 rounded-md border border-danger-ring bg-danger-soft px-3.5 py-3 text-sm text-danger-text">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          {error}
        </p>
      )}

      {disputes.isLoading ? (
        <div className="flex justify-center py-12">
          <Spinner />
        </div>
      ) : disputes.isError ? (
        <p role="alert" className="rounded-md border border-danger-ring bg-danger-soft px-3.5 py-3 text-sm text-danger-text">
          Could not load your disputes.{' '}
          {disputes.error instanceof ApiClientError ? disputes.error.message : 'The request failed.'}
        </p>
      ) : rows.length === 0 ? (
        <div className="panel">
          <EmptyState
            title={status === 'OPEN' ? 'No open disputes' : 'Nothing matches this filter'}
            description="Chargebacks land here the moment an issuer raises one, with the deadline to respond."
          />
        </div>
      ) : (
        <ul className="space-y-3">
          {rows.map((dispute) => {
            const open = openId === dispute.id;
            const answerable = dispute.status === 'EVIDENCE_REQUIRED' || dispute.status === 'OPEN';
            return (
              <li key={dispute.id} className="rounded-lg border border-line bg-surface-raised">
                <div className="flex flex-wrap items-start gap-4 p-4 sm:p-5">
                  <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-line bg-surface-inset">
                    <Gavel className="h-4 w-4 text-content-subtle" aria-hidden="true" />
                  </span>

                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2.5">
                      <Money amount={dispute.amount.amount} currency={dispute.amount.currency} className="text-md font-semibold" />
                      <StatusPill status={dispute.status} />
                      {answerable && <Deadline days={dispute.days_to_respond} />}
                    </div>

                    <p className="mt-2 text-xs text-content-subtle">
                      {enumLabel(dispute.category)}
                      {dispute.reason_code && ` · reason ${dispute.reason_code}`}
                      {' · '}
                      <span className="numeral">{dispute.payment_id}</span>
                      {dispute.payment_method && ` · ${enumLabel(dispute.payment_method)}`}
                      {' · raised '}
                      {relativeTime(dispute.created_at)}
                    </p>

                    {dispute.evidence.narrative && (
                      <details className="mt-3">
                        <summary className="cursor-pointer text-xs text-content-muted">
                          Your response · submitted {relativeTime(dispute.evidence.submitted_at)}
                        </summary>
                        <p className="mt-2 whitespace-pre-wrap rounded-md border border-line bg-surface-inset px-3.5 py-3 text-xs leading-relaxed text-content-muted">
                          {dispute.evidence.narrative}
                        </p>
                      </details>
                    )}

                    {dispute.resolution_note && (
                      <p
                        className={`mt-3 rounded-md px-3.5 py-2.5 text-xs leading-relaxed ${
                          dispute.status === 'WON'
                            ? 'border border-brand-800 bg-brand-soft text-brand-300'
                            : 'border border-danger-ring bg-danger-soft text-danger-text'
                        }`}
                      >
                        {dispute.resolution_note}
                      </p>
                    )}
                  </div>

                  {answerable && !open && (
                    <button
                      type="button"
                      onClick={() => {
                        setOpenId(dispute.id);
                        setNarrative('');
                        setError(null);
                      }}
                      className="btn btn-primary btn-sm shrink-0"
                    >
                      <ShieldQuestion className="h-3.5 w-3.5" aria-hidden="true" />
                      Respond
                    </button>
                  )}
                </div>

                {open && (
                  <div className="border-t border-line bg-surface-inset/40 p-4 sm:p-5">
                    <label htmlFor={`evidence-${dispute.id}`} className="label">
                      Your account of the transaction
                    </label>
                    <textarea
                      id={`evidence-${dispute.id}`}
                      rows={5}
                      value={narrative}
                      onChange={(e) => setNarrative(e.target.value)}
                      placeholder="What was ordered, when it was delivered, and how you know the cardholder authorised it. Include tracking numbers, delivery confirmations, login timestamps and any prior undisputed orders from the same customer."
                      className="input h-auto py-2.5 leading-relaxed"
                    />
                    <p className="mt-2 text-2xs leading-relaxed text-content-faint">
                      This text is sent to the issuing bank as written. Specific facts with references win cases;
                      assertions do not. {narrative.trim().length}/40 characters minimum.
                    </p>

                    <div className="mt-4 flex items-center justify-end gap-2 border-t border-line pt-4">
                      <button type="button" onClick={() => setOpenId(null)} className="btn btn-ghost btn-sm">
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={() => submit.mutate(dispute.id)}
                        disabled={submit.isPending || narrative.trim().length < 40}
                        className="btn btn-primary btn-sm"
                      >
                        {submit.isPending ? <Spinner /> : <Send className="h-3.5 w-3.5" aria-hidden="true" />}
                        Submit response
                      </button>
                    </div>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <p className="flex items-start gap-2 border-t border-line pt-4 text-2xs leading-relaxed text-content-faint">
        <Check className="mt-0.5 h-3 w-3 shrink-0" aria-hidden="true" />
        Won and withdrawn cases return the held amount to your available balance on the same day they close.
      </p>
    </div>
  );
}
